import React, { useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiCheck,
  FiInfo,
  FiSquare,
  FiCheckSquare,
  FiSearch,
} from "react-icons/fi";
import { GraphContext } from "../../../../../../contexts/GraphContext";
import { toast } from "react-hot-toast";
import { Tooltip } from "@mui/material";

export const ValueActivationModal = ({
  isOpen,
  onClose,
  tagData,
  onUpdate,
}) => {
  const {
    discoveryGraphData,
    updateDiscoveryGraphData,
    handleSaveGraph,
    selectedDiscoveryGraph,
  } = useContext(GraphContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeValues, setActiveValues] = useState(new Set());
  const [initialActiveValues, setInitialActiveValues] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [childrenWarnings, setChildrenWarnings] = useState({});
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [warnings, setWarnings] = useState([]);

  // Get current tag data from graph
  const getCurrentTagData = () => {
    if (!discoveryGraphData || !tagData?.nodePath) return null;

    let current = discoveryGraphData;
    for (const pathItem of tagData.nodePath) {
      current = current[pathItem];
      if (!current) return null;
    }
    return current;
  };

  // Check if a value has children
  const checkValueHasChildren = (value) => {
    const currentTagData = getCurrentTagData();
    if (!currentTagData || !currentTagData[value]) return false;

    // Check if the value node has any child tags
    const valueNode = currentTagData[value];
    return Object.keys(valueNode).some(
      (key) =>
        key !== "TagAvailableValues" &&
        valueNode[key]?.TagAvailableValues !== undefined,
    );
  };

  // Count children for a value
  const countChildrenForValue = (value) => {
    const currentTagData = getCurrentTagData();
    if (!currentTagData || !currentTagData[value]) return 0;

    let count = 0;
    const countTags = (node) => {
      Object.keys(node).forEach((key) => {
        if (
          key !== "TagAvailableValues" &&
          node[key]?.TagAvailableValues !== undefined
        ) {
          count++;
          countTags(node[key]);
        }
      });
    };

    countTags(currentTagData[value]);
    return count;
  };

  // Initialize active values from graph data
  useEffect(() => {
    if (!isOpen) return;

    const currentTagData = getCurrentTagData();
    if (!currentTagData) return;

    // Active values are those that exist as nodes in the graph
    const activeVals = new Set();
    const warnings = {};

    // Use availableValues from tagData if provided, otherwise fall back to graph data
    const availableValues =
      tagData.availableValues || currentTagData.TagAvailableValues || [];

    availableValues.forEach((value) => {
      if (value !== "<ANY>" && currentTagData[value]) {
        activeVals.add(value);
        // Check if this value has children
        if (checkValueHasChildren(value)) {
          warnings[value] = countChildrenForValue(value);
        }
      }
    });

    setActiveValues(activeVals);
    setInitialActiveValues(new Set(activeVals));
    setChildrenWarnings(warnings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, discoveryGraphData, tagData]);

  const handleToggleValue = (value) => {
    const newActiveValues = new Set(activeValues);
    if (newActiveValues.has(value)) {
      newActiveValues.delete(value);
    } else {
      newActiveValues.add(value);
    }
    setActiveValues(newActiveValues);
  };

  const handleSelectAll = () => {
    const currentTagData = getCurrentTagData();
    if (!currentTagData?.TagAvailableValues) return;

    const allValues = currentTagData.TagAvailableValues.filter(
      (v) => v !== "<ANY>",
    );
    setActiveValues(new Set(allValues));
  };

  const handleDeselectAll = () => {
    setActiveValues(new Set());
  };

  const proceedWithSave = async () => {
    setIsLoading(true);
    try {
      // Create a deep copy of the data structure
      const updatedData = JSON.parse(JSON.stringify(discoveryGraphData));
      let current = updatedData;

      // Navigate to the tag
      for (const pathItem of tagData.nodePath) {
        current = current[pathItem];
      }

      // Get all available values - prefer tagData.availableValues over graph data
      const availableValues =
        tagData.availableValues || current.TagAvailableValues || [];

      // Process each available value
      availableValues.forEach((value) => {
        if (value === "<ANY>") return;

        if (activeValues.has(value)) {
          // Value should be active - ensure it exists in graph
          if (!current[value]) {
            current[value] = {};
          }
        } else {
          // Value should be inactive - remove it from graph
          delete current[value];
        }
      });

      // Update the TagAvailableValues in the graph to include all available values
      current.TagAvailableValues = availableValues;

      // If no active values, ensure we have <ANY> node
      const hasActiveValues = availableValues.some(
        (v) => v !== "<ANY>" && activeValues.has(v),
      );
      if (!hasActiveValues && !current["<ANY>"]) {
        current["<ANY>"] = {};
      }

      // Update the graph
      updateDiscoveryGraphData(updatedData);

      // Save the graph
      if (selectedDiscoveryGraph) {
        await handleSaveGraph({
          ...selectedDiscoveryGraph,
          graph_data: updatedData,
        });
      }

      // Notify parent component
      if (onUpdate) {
        onUpdate(activeValues);
      }

      toast.success(`Updated target values for "${tagData.label}"`);
      onClose();
    } catch (error) {
      console.error("Error saving target values:", error);
      toast.error("Failed to save target values");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    const newWarnings = [];

    // Warning 1: Tag becomes open-ended
    if (activeValues.size === 0 && initialActiveValues.size > 0) {
      newWarnings.push(
        "Deactivating all values will make this tag open-ended, extracting text instead of classifying into specific values.",
      );
    }

    // Warning 2: Deactivating values with nested tags
    const deactivatedValuesWithChildren = [];
    initialActiveValues.forEach((value) => {
      if (!activeValues.has(value) && childrenWarnings[value]) {
        deactivatedValuesWithChildren.push({
          value,
          count: childrenWarnings[value],
        });
      }
    });

    if (deactivatedValuesWithChildren.length > 0) {
      deactivatedValuesWithChildren.forEach((item) => {
        newWarnings.push(
          `Deactivating "${item.value}" will remove ${item.count} nested tag(s).`,
        );
      });
    }

    if (newWarnings.length > 0) {
      setWarnings(newWarnings);
      setIsWarningModalOpen(true);
    } else {
      await proceedWithSave();
    }
  };

  if (!isOpen) return null;

  const currentTagData = getCurrentTagData();
  const allValues = (
    tagData.availableValues ||
    currentTagData?.TagAvailableValues ||
    []
  ).filter((v) => v !== "<ANY>");
  const filteredValues = allValues.filter((value) =>
    value.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const activeCount = activeValues.size;
  const totalCount = allValues.length;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1001] animate-fadeIn"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 shadow-xl animate-slideIn">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Manage Target Values
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Tag:{" "}
                <span className="font-medium text-primary">
                  {tagData.label}
                </span>
              </p>
              <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2 border border-amber-200">
                <strong>Important:</strong> When running this tag, only target
                values will be used for extraction. If this tag extracts value
                "X", any nested tags under "X" will also be extracted.
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <FiX size={20} />
            </button>
          </div>

          {/* Active/Total Counter */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Target Values:</span>
              <span className="text-lg font-semibold text-primary">
                {activeCount} / {totalCount}
              </span>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${totalCount > 0 ? (activeCount / totalCount) * 100 : 0}%`,
                }}
              />
            </div>
            {activeCount === 0 && totalCount > 0 && (
              <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-2">
                <FiInfo className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  When no values are activated, this tag will function as an
                  open-ended tag, extracting the best matching text from
                  documents instead of classifying into specific values.
                </p>
              </div>
            )}
            {activeCount > 0 && Object.keys(childrenWarnings).length > 0 && (
              <div className="mt-3 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md p-2">
                <FiInfo className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  <strong>Nested Tag Extraction:</strong> When this tag extracts
                  an active value (e.g., "Phase 2"), any sub-tags defined under
                  that value will automatically be extracted from the same
                  document.
                </p>
              </div>
            )}
          </div>

          {/* Search Box */}
          <div className="relative mb-4">
            <FiSearch
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search values..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          {/* Bulk Actions */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={handleSelectAll}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors"
            >
              <FiCheckSquare size={14} />
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              <FiSquare size={14} />
              Deselect All
            </button>
          </div>

          {/* Values List */}
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
            {filteredValues.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {searchTerm
                  ? "No values match your search"
                  : "No values available"}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredValues.map((value) => {
                  const isActive = activeValues.has(value);
                  return (
                    <div
                      key={value}
                      onClick={() => handleToggleValue(value)}
                      className={`
                        flex items-center justify-between px-4 py-2.5 cursor-pointer
                        transition-colors duration-150
                        ${
                          isActive
                            ? "bg-primary/5 hover:bg-primary/10"
                            : "hover:bg-gray-50"
                        }
                      `}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <span
                          className={`text-sm ${isActive ? "text-gray-900 font-medium" : "text-gray-600"}`}
                        >
                          {value}
                        </span>
                        {childrenWarnings[value] && (
                          <Tooltip
                            title={`When "${value}" is extracted, ${childrenWarnings[value]} nested tag${childrenWarnings[value] > 1 ? "s" : ""} will also be extracted automatically`}
                            arrow
                            placement="right"
                          >
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              {childrenWarnings[value]} nested tag
                              {childrenWarnings[value] > 1 ? "s" : ""}
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <div
                        className={`
                        w-5 h-5 rounded border-2 flex items-center justify-center
                        transition-all duration-150
                        ${
                          isActive
                            ? "bg-primary border-primary"
                            : "border-gray-300 hover:border-gray-400"
                        }
                      `}
                      >
                        {isActive && (
                          <FiCheck size={12} className="text-white" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-6">
            <p className="text-xs text-gray-500 text-left">
              {activeCount === 0 && totalCount > 0
                ? "No target values - tag will extract any matching text"
                : `${activeCount} active value${activeCount !== 1 ? "s" : ""} - only these will be used during extraction${activeCount > 0 ? ". Any nested tags under these values will also extract." : ""}`}
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={() => setIsWarningModalOpen(false)}
        onConfirm={proceedWithSave}
        warnings={warnings}
      />
    </>,
    document.body,
  );
};

const WarningModal = ({ isOpen, onClose, onConfirm, warnings }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1002]">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl animate-slideIn">
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10">
            <FiInfo className="h-6 w-6 text-amber-600" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
            <h3
              className="text-lg leading-6 font-medium text-gray-900"
              id="modal-title"
            >
              Confirm Changes
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                Please review the following potential consequences of your
                changes:
              </p>
              <ul className="mt-3 list-disc list-inside space-y-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50 sm:ml-3 sm:w-auto sm:text-sm"
            onClick={onConfirm}
          >
            Confirm & Save
          </button>
          <button
            type="button"
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 sm:mt-0 sm:w-auto sm:text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Helper function to get active values for a tag
export const getActiveValues = (discoveryGraphData, nodePath) => {
  if (!discoveryGraphData || !nodePath) return [];

  let current = discoveryGraphData;
  for (const pathItem of nodePath) {
    current = current[pathItem];
    if (!current) return [];
  }

  // Active values are those that exist as nodes in the graph
  const activeValues = [];
  if (current.TagAvailableValues) {
    current.TagAvailableValues.forEach((value) => {
      if (value !== "<ANY>" && current[value]) {
        activeValues.push(value);
      }
    });
  }

  return activeValues;
};
